import { Download } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { listFileProblem } from "@/lib/campaign";

/**
 * Hidden until the import is wired up. Flip to `true` to put the button back on the toolbar.
 *
 * It is off rather than deleted because the control itself is the part worth keeping: the old
 * button was a `<Link to="/campaigns">`, so pressing Import left the board instead of importing
 * anything. It takes a CSV now, and showing it is a one-line change once a chosen file has
 * somewhere to go.
 */
const IMPORT_VISIBLE = false;

/**
 * "Import" on the opportunities toolbar: opens a CSV file picker and hands the file to `onFile`.
 *
 * There is deliberately no fallback for leaving `onFile` out — the only import the API exposes is
 * `POST /campaigns/{id}/lists`, which also needs a campaign and a list name, neither of which the
 * board knows. Until that seam exists the control stays hidden, so a file is never taken in a way
 * that looks like it imported something.
 */
export function ImportOpportunitiesButton({
  canImport, onFile, visible = IMPORT_VISIBLE,
}: {
  /** A supervisor or an admin. Importing leads is not a rep's to do. */
  canImport: boolean;
  onFile?: (file: File) => void;
  /** The switch, defaulted from `IMPORT_VISIBLE`. App code leaves it alone; tests set it. */
  visible?: boolean;
}) {
  const toast = useToast();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const choose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // The same input serves the next pick, and a browser fires no change event when the identical
    // file is chosen twice. Clearing it keeps a corrected re-pick working.
    event.target.value = "";
    if (!file) return;
    const problem = listFileProblem(file);
    if (problem) {
      toast.error("That file cannot be imported", problem);
      return;
    }
    onFile?.(file);
  };

  if (!visible || !canImport) return null;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={choose}
        aria-hidden
        tabIndex={-1}
      />
      <Button
        variant="outline"
        className="h-9 text-accent-11"
        onClick={() => inputRef.current?.click()}
        title="Import a CSV of opportunities"
      >
        <Download />
        Import
      </Button>
    </>
  );
}
